/**
 * Small presentational pieces the messaging screens share — all styled from
 * the app's `--color-*` tokens (dark and light themes), IBM Plex from the
 * host, text never below 13px, inputs on `--color-input-bg` with a
 * `--color-border-strong` border at 36px, labels weight 600, cards on
 * `--color-bg-secondary`. Buttons use the host's own `.btn` family.
 */
import type { CSSProperties, ReactNode } from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react'

export const input: CSSProperties = {
  height: 36, boxSizing: 'border-box', padding: '0 10px', borderRadius: 6, fontSize: 14,
  border: '1px solid var(--color-border-strong, #c9d1db)', background: 'var(--color-input-bg, #fff)',
  color: 'var(--color-text, #1b2330)', fontFamily: 'inherit', minWidth: 0,
}

export const textarea: CSSProperties = {
  ...input, height: 'auto', minHeight: 90, padding: '8px 10px', lineHeight: 1.6, resize: 'vertical',
}

export const label: CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary, #465062)',
  marginBottom: 4,
}

export const meta: CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary, #465062)' }

export const mono: CSSProperties = {
  fontFamily: "'IBM Plex Mono', var(--font-mono, ui-monospace, monospace)", fontSize: 13,
}

export const card: CSSProperties = {
  background: 'var(--color-bg-secondary, #fafbfc)', border: '1px solid var(--color-border, #d8dee6)',
  borderRadius: 8,
}

export const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
export const th: CSSProperties = {
  textAlign: 'left', padding: '7px 10px', fontSize: 13, fontWeight: 600,
  color: 'var(--color-text-secondary, #465062)', background: 'var(--color-bg-secondary, #f0f3f7)',
  borderBottom: '1px solid var(--color-border, #d8dee6)', whiteSpace: 'nowrap',
}
export const td: CSSProperties = {
  padding: '7px 10px', borderBottom: '1px solid var(--color-border, #e6eaef)', verticalAlign: 'top',
  color: 'var(--color-text, #1b2330)',
}

export const divider: CSSProperties = {
  width: 1, alignSelf: 'stretch', minHeight: 24, background: 'var(--color-border, #d8dee6)', margin: '0 4px',
}

/** A field label above its control. */
export function Field({ title, htmlFor, children, style }: {
  title: string; htmlFor?: string; children: ReactNode; style?: CSSProperties
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, ...style }}>
      <label htmlFor={htmlFor} style={label}>{title}</label>
      {children}
    </div>
  )
}

/** A segmented control (one of a few). */
export function Seg<T extends string>({ options, value, onChange, ariaLabel, disabled }: {
  options: { value: T; label: string; title?: string }[]
  value: T
  onChange: (v: T) => void
  ariaLabel: string
  disabled?: boolean
}) {
  return (
    <div role="group" aria-label={ariaLabel} style={{
      display: 'inline-flex', border: '1px solid var(--color-border-strong, #c9d1db)', borderRadius: 6,
      overflow: 'hidden', flexShrink: 0,
    }}>
      {options.map((o, i) => {
        const on = o.value === value
        return (
          <button key={o.value} type="button" disabled={disabled} title={o.title}
                  aria-pressed={on} onClick={() => onChange(o.value)}
                  style={{
                    font: 'inherit', fontSize: 13, padding: '0 12px', minHeight: 32, border: 0, cursor: 'pointer',
                    borderLeft: i ? '1px solid var(--color-border-strong, #c9d1db)' : 0,
                    background: on ? 'var(--color-accent, #0061D0)' : 'var(--color-input-bg, #fff)',
                    color: on ? '#fff' : 'var(--color-text, #1b2330)', fontWeight: on ? 600 : 400,
                  }}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

/** A toggle chip (channel on/off, starter template, …). */
export function Chip({ on, disabled, onClick, title, children }: {
  on?: boolean; disabled?: boolean; onClick?: () => void; title?: string; children: ReactNode
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} aria-pressed={!!on}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, padding: '0 12px',
              minHeight: 32, fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
              cursor: disabled ? 'not-allowed' : 'pointer',
              border: `1px ${disabled ? 'dashed' : 'solid'} ${on ? 'var(--color-accent, #0061D0)' : 'var(--color-border-strong, #c9d1db)'}`,
              background: on ? 'var(--color-accent-weak, rgba(0,97,208,.10))' : 'var(--color-input-bg, #fff)',
              color: disabled ? 'var(--color-text-secondary, #687281)' : on ? 'var(--color-accent, #0052b0)' : 'var(--color-text, #1b2330)',
            }}>
      {children}
    </button>
  )
}

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'error'

const TONES: Record<Tone, { bg: string; fg: string }> = {
  neutral: { bg: 'var(--color-bg-secondary, #f0f3f7)', fg: 'var(--color-text-secondary, #465062)' },
  accent: { bg: 'var(--color-accent-weak, #e3f0ff)', fg: 'var(--color-accent, #0052b0)' },
  success: { bg: 'var(--color-success-weak, #e6f4ea)', fg: 'var(--color-success, #1a6b33)' },
  warning: { bg: 'var(--color-warning-weak, #fff4e5)', fg: 'var(--color-warning, #7a4a00)' },
  error: { bg: 'var(--color-error-weak, #fdecea)', fg: 'var(--color-error, #a4161a)' },
}

export function Pill({ tone = 'neutral', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  const t = TONES[tone]
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 13, fontWeight: 600, borderRadius: 4,
      padding: '1px 8px', background: t.bg, color: t.fg, whiteSpace: 'nowrap',
    }}>{children}</span>
  )
}

/** A boxed note (warnings, check-before-sending lines, errors). */
export function Note({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  const t = TONES[tone]
  const Icon = tone === 'error' ? AlertCircle : tone === 'warning' ? AlertTriangle
    : tone === 'success' ? CheckCircle2 : Info
  return (
    <div role={tone === 'error' ? 'alert' : undefined} style={{
      display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5,
      padding: '8px 10px', borderRadius: 6, background: t.bg, color: t.fg,
    }}>
      <Icon size={15} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  )
}

/** An error with the server's problem list. */
export function ErrorNote({ error }: { error: { message: string; problems: string[] } | null }) {
  if (!error) return null
  return (
    <Note tone="error">
      <div style={{ fontWeight: 600 }}>{error.message}</div>
      {error.problems.length > 0 && (
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          {error.problems.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}
    </Note>
  )
}

/** A switch (on/off). */
export function Switch({ on, onChange, label: aria, disabled }: {
  on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean
}) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={aria} disabled={disabled}
            onClick={() => onChange(!on)}
            style={{
              position: 'relative', width: 38, height: 22, borderRadius: 999, border: 0, flexShrink: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: on ? 'var(--color-accent, #0061D0)' : 'var(--color-border-strong, #c9d1db)',
            }}>
      <span style={{
        position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: '50%',
        background: '#fff', transition: 'left .12s',
      }} />
    </button>
  )
}

/** Tabs along a bottom border. */
export function TabBar<T extends string>({ tabs, value, onChange, ariaLabel }: {
  tabs: { value: T; label: ReactNode }[]; value: T; onChange: (v: T) => void; ariaLabel: string
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} style={{ display: 'flex', flexWrap: 'wrap' }}>
      {tabs.map((t) => {
        const on = t.value === value
        return (
          <button key={t.value} role="tab" type="button" aria-selected={on} onClick={() => onChange(t.value)}
                  style={{
                    font: 'inherit', fontSize: 14, padding: '8px 14px', minHeight: 40, border: 0,
                    borderBottom: `2px solid ${on ? 'var(--color-accent, #0061D0)' : 'transparent'}`,
                    background: 'transparent', cursor: 'pointer', fontWeight: on ? 600 : 400,
                    color: on ? 'var(--color-accent, #0052b0)' : 'var(--color-text-secondary, #465062)',
                  }}>
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

export const STATUS_TONES: Record<string, Tone> = {
  sent: 'success', done: 'success', failed: 'error', skipped: 'warning', partial: 'warning',
  queued: 'neutral', sending: 'accent', running: 'accent', expanding: 'accent',
}

export const STATUS_LABELS: Record<string, string> = {
  sent: 'Sent', failed: 'Failed', skipped: 'Skipped', queued: 'Queued', sending: 'Sending',
  done: 'Done', partial: 'Partly sent', running: 'Sending', expanding: 'Preparing',
}

export function fmtWhen(iso?: string | null): string {
  if (!iso) return ''
  const s = String(iso).replace(' ', 'T')
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function fmtSize(bytes?: number): string {
  const n = Number(bytes || 0)
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  if (n >= 1024) return `${Math.round(n / 1024)} KB`
  return `${n} B`
}

/** The full-window overlay frame every messaging window sits in. */
export const overlay: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 9700, display: 'flex', alignItems: 'stretch', justifyContent: 'center',
  background: 'rgba(15, 20, 28, 0.55)', padding: '24px 16px', boxSizing: 'border-box', overflow: 'auto',
}

export const windowFrame: CSSProperties = {
  width: '100%', maxWidth: 1300, margin: 'auto 0', background: 'var(--color-bg, #fff)',
  color: 'var(--color-text, #1b2330)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.35)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden', fontSize: 14, minHeight: 0,
  border: '1px solid var(--color-border, #d8dee6)',
}

export const windowHeader: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', flexWrap: 'wrap',
  borderBottom: '1px solid var(--color-border, #d8dee6)',
}
